import {
  ProfileConfiguration,
  Profile,
  ProfileCollectionModes,
  AnalyticsProfileCollectionSettings,
} from '../../shared/features/ng-redux/profiles/profiles.models';
import {
  IProfileConfiguration,
  IProfile,
  IAgentConfigurationEntry,
} from '../../../app/modules/analytics-profiles/profile-contract';
import { DATA_DELIVERY_INTERVALS, DATA_DELIVERY_WINDOW_SIZES } from 'shared/models/analytics-profiiles.ui.params.model';

const MIN_PER_HOUR = 60;

export class ProfilesMapper {
  static mapToProfile(profile: IProfile): Profile {
    return <Profile>{
      id: profile.profileId,
      name: profile.name,
      mcInstanceId: profile.mcConnectionId,
      description: profile.description,
      isActive: profile.isEnabled,
      hasGroups: profile.hasGroups !== 0,
      osId: profile.osId,
    };
  }

  static mapToProfileMetadata(profile: ProfileConfiguration): Profile {
    return {
      description: profile.description,
      hasGroups: profile.deviceGroupIds && profile.deviceGroupIds.length > 0,
      id: profile.id,
      isActive: profile.isActive,
      mcInstanceId: profile.mcInstanceId,
      name: profile.name,
      osId: profile.osId,
      isVisible: profile.isVisible
    };
  }

  static mapToConfiguration(profile: IProfileConfiguration): ProfileConfiguration {
    let result: ProfileConfiguration = {
      id: profile.profileId,
      name: profile.profileName,
      description: profile.profileDescription,
      isActive: profile.isEnabled,
      mcInstanceId: profile.mcConnectionId,
      deviceGroupIds: profile.groupList,
      deviceGroupIdsSet: new Set(profile.groupList),
      collectionSettings: ProfilesMapper.mapToCollectionSettings(profile),
      osId: profile.osId,
      rolePermissions: [],
      isVisible: profile.isVisible
    };
    return result;
  }

  static mapFromConfiguration(profile: ProfileConfiguration): IProfileConfiguration {
    const { mode, entries } = ProfilesMapper.mapFromCollectionSettings(profile);
    let result: IProfileConfiguration = {
      profileId: profile.id== null? undefined: profile.id,
      profileName: profile.name,
      profileDescription: profile.description,
      isEnabled: profile.isActive,
      mcConnectionId: profile.mcInstanceId,
      groupList: profile.deviceGroupIds,
      collectionModeId: mode,
      agentConfiguration: entries,
      osId: profile.osId,
      rolePermissions: profile.rolePermissions,
      isVisible: profile.isVisible
    };
    return result;
  }

  private static mapToCollectionSettings(profile: IProfileConfiguration): AnalyticsProfileCollectionSettings {
    const settings = new AnalyticsProfileCollectionSettings();
    settings.collectionMode = ProfilesMapper.mapToCollectionMode(profile.collectionModeId);

    if (!!profile.agentConfiguration) {
      for (const entry of profile.agentConfiguration) {
        switch (entry.name) {
          case 'uploadCellular':
            settings.dataUpload.cellular = JSON.parse(entry.value);
            break;
          case 'uploadRoaming':
            settings.dataUpload.cellularWhileRoaming = JSON.parse(entry.value);
            break;
          case 'cookersPeriod':
            settings.dataDeliveryInterval = this.mapToDeliveryInterval(entry.value);
            break;
          case 'cookedMsgSendPeriod':
            settings.dataDeliveryWindowSize = this.mapToDeliveryWindowSize(entry.value);
            break;
        }
      }
      return settings;
    }
  }

  private static mapFromCollectionSettings(profile: ProfileConfiguration): {
    mode: number;
    entries: IAgentConfigurationEntry[];
  } {
    const entries: IAgentConfigurationEntry[] = [];
    const collectionSettings = profile.collectionSettings;

    entries.push({
      name: 'uploadCellular',
      value: JSON.stringify(collectionSettings.dataUpload.cellular),
      customModeOnly: 0,
    });
    entries.push({
      name: 'uploadRoaming',
      value: JSON.stringify(collectionSettings.dataUpload.cellularWhileRoaming),
      customModeOnly: 0,
    });
    entries.push({
      name: 'cookersPeriod',
      value: ProfilesMapper.mapFromDeliveryInterval(collectionSettings.dataDeliveryInterval),
      customModeOnly: 0,
    });
    entries.push({
      name: 'cookedMsgSendPeriod',
      value: ProfilesMapper.mapFromDeliveryWindowSize(collectionSettings.dataDeliveryWindowSize),
      customModeOnly: 0,
    });

    return {
      mode: ProfilesMapper.mapFromCollectionMode(profile.collectionSettings.collectionMode),
      entries,
    };
  }

  // TODO: later map from here: http://localhost:8048/profiles/config
  private static mapToCollectionMode(collectionModeId: number): ProfileCollectionModes {
    switch (collectionModeId) {
      case 1:
        return 'standard';
      case 2:
        return 'battery-optimized';
      case 3:
        return 'accuracy-optimized';
    }
  }

  private static mapFromCollectionMode(collectionMode: ProfileCollectionModes): number {
    switch (collectionMode) {
      case 'standard':
        return 1;
      case 'battery-optimized':
        return 2;
      case 'accuracy-optimized':
        return 3;
    }
  }

  // cookersPeriod (min) -> deliveryInterval (hours)
  // Old value can be anything in terms of minutes hence
  // we do rounding to map to the nearest of allowed values
  private static mapToDeliveryInterval(cookersPeriod: string): number {
    const mappedValue = Math.round(parseInt(cookersPeriod) / MIN_PER_HOUR);
    const possibleValues = DATA_DELIVERY_INTERVALS.map(entry => entry.key);
    const nearestValue = possibleValues.reduce((prev, curr) =>
      Math.abs(curr - mappedValue) < Math.abs(prev - mappedValue) ? curr : prev
    );
    return nearestValue;
  }

  // deliveryInterval (hours) -> cookersPeriod (min)
  private static mapFromDeliveryInterval(deliveryInterval: number): string {
    return (deliveryInterval * 60).toString();
  }

  // cookedMsgSendPeriod (min) -> deliveryWindowSize (hours)
  // Old value can be anything in terms of minutes hence
  // we do rounding to map to the nearest of allowed values
  private static mapToDeliveryWindowSize(cookedMsgSendPeriod: string): number {
    const mappedValue = Math.round(parseInt(cookedMsgSendPeriod) / MIN_PER_HOUR);
    const possibleValues = DATA_DELIVERY_WINDOW_SIZES.map(entry => entry.key);
    const nearestValue = possibleValues.reduce((prev, curr) =>
      Math.abs(curr - mappedValue) < Math.abs(prev - mappedValue) ? curr : prev
    );
    return nearestValue;
  }

  // deliveryWindowSize (hours) -> cookedMsgSendPeriod (min)
  private static mapFromDeliveryWindowSize(deliveryWindowSize: number): string {
    return (deliveryWindowSize * 60).toString();
  }


}
